import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  LivePost, LiveViewersPayload, LiveChatMessage, ReactionCounts, ReactionKey
} from '../services/liveService';

const SOCKET_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '');

interface Handlers {
  onCreated?: (post: LivePost) => void;
  onUpdated?: (post: LivePost) => void;
  onDeleted?: (postId: string) => void;
  onStatus?: (payload: { status: string; startedAt?: string | null; accumulatedSeconds?: number }) => void;
  onViewers?: (payload: LiveViewersPayload) => void;
  onChatMessage?: (msg: LiveChatMessage) => void;
  onChatDeleted?: (messageId: string) => void;
  onReactionsEvent?: (counts: ReactionCounts) => void;
  onReactionsPost?: (payload: { postId: string; counts: ReactionCounts }) => void;
  onFloatingEmoji?: (emoji: ReactionKey) => void;
}

// token: passato per dirette riservate o per identificare l'utente nella presence
export function useLiveSocket(slug: string | undefined, handlers: Handlers, token?: string) {
  const ref = useRef<Socket | null>(null);
  const h = useRef(handlers);
  h.current = handlers;

  useEffect(() => {
    if (!slug) return;
    const socket = io(`${SOCKET_BASE}/live`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: token ? { token } : {}
    });
    ref.current = socket;

    socket.on('connect', () => socket.emit('join_live', { slug }));
    socket.on('live_post_created', (p: LivePost) => h.current.onCreated?.(p));
    socket.on('live_post_updated', (p: LivePost) => h.current.onUpdated?.(p));
    socket.on('live_post_deleted', ({ postId }: { postId: string }) => h.current.onDeleted?.(postId));
    socket.on('live_status_changed', (payload: { status: string; startedAt?: string | null; accumulatedSeconds?: number }) => h.current.onStatus?.(payload));
    socket.on('live_viewers', (payload: LiveViewersPayload) => h.current.onViewers?.(payload));
    socket.on('live_chat_message', (msg: LiveChatMessage) => h.current.onChatMessage?.(msg));
    socket.on('live_chat_deleted', ({ messageId }: { messageId: string }) => h.current.onChatDeleted?.(messageId));
    socket.on('live_reactions_event', (counts: ReactionCounts) => h.current.onReactionsEvent?.(counts));
    socket.on('live_reactions_post', (p: { postId: string; counts: ReactionCounts }) => h.current.onReactionsPost?.(p));
    socket.on('live_floating_emoji', ({ emoji }: { emoji: ReactionKey }) => h.current.onFloatingEmoji?.(emoji));

    return () => { socket.disconnect(); ref.current = null; };
  }, [slug, token]);
}
